import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, BarChart3, Target, FileText, TrendingUp, Share2, Megaphone, Check, X
} from 'lucide-react';
// src/component/homepage/homepage.component.jsx
import { submitDemoRequest, transformDemoRequestData } from '../../service/demo-request.service';
import { trackSeoInsight, storeInsightId, getStoredInsightId } from '../../service/seo-insight-tracking.service';
import { getActiveTestimonials } from '../../service/testimonial.service';


import Navbar from '../Common/Navbar';
import SecondaryFooter from '../Common/SecondaryFooter';


import './homepage.component.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import {
  getAllCategory,
  getAllProducts,
  getProductFeatures,
  getProductById,
  getFeatureByProduct
} from '../../service/product.service';

// ============= DEMO MODAL COMPONENT =============
const DemoRequestModal = ({ isOpen, onClose, productName }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    userType: '',
    mobileNumber: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.userType) {
      newErrors.userType = 'Please select a user type';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber.trim())) {
      newErrors.mobileNumber = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = transformDemoRequestData(formData, productName);
      const result = await submitDemoRequest(payload);

      console.log('Demo request submitted successfully:', result);
      setSubmitSuccess(true);

      setTimeout(() => {
        setFormData({
          firstName: '',
          userType: '',
          mobileNumber: '',
          email: '',
          message: ''
        });
        setSubmitSuccess(false);
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting demo request:', error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.title ||
        error.message ||
        'Something went wrong. Please try again.';
      setErrors({ submit: `❌ ${errorMessage}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        firstName: '',
        userType: '',
        mobileNumber: '',
        email: '',
        message: ''
      });
      setErrors({});
      setSubmitSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="demo-modal-overlay" onClick={handleClose}>
      <div className="demo-modal-container" onClick={e => e.stopPropagation()}>
        <button
          className="demo-modal-close"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        <div className="demo-modal-header">
          <h2>Request Free Demo</h2>
          <p>Fill in your details and we'll get back to you shortly</p>
          {productName && (
            <div className="selected-product-badge">
              Product: <strong>{productName}</strong>
            </div>
          )}
        </div>

        {submitSuccess ? (
          <div className="demo-success-message">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h3>Request Submitted Successfully!</h3>
            <p>✅ Thank you for requesting a demo! We will get back to you soon.</p>
          </div>
        ) : (
          <form className="demo-modal-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'error' : ''}
                placeholder="Enter your first name"
                disabled={isSubmitting}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="userType">
                User Type <span className="required">*</span>
              </label>
              <select
                id="userType"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                className={errors.userType ? 'error' : ''}
                disabled={isSubmitting}
              >
                <option value="">Select Type</option>
                <option value="Individual">Individual</option>
                <option value="Organisation">Organisation</option>
              </select>
              {errors.userType && (
                <span className="error-message">{errors.userType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="mobileNumber">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                className={errors.mobileNumber ? 'error' : ''}
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
                disabled={isSubmitting}
              />
              {errors.mobileNumber && (
                <span className="error-message">{errors.mobileNumber}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email ID <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email address"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your requirements (optional)"
                rows="4"
                disabled={isSubmitting}
              />
            </div>

            {errors.submit && (
              <div className="submit-error">{errors.submit}</div>
            )}

            <button
              type="submit"
              className="demo-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Demo'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ============= HOMEPAGE COMPONENT =============
const HomePageComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const [categories, setCategories] = useState([]);
  const [productList, setProductList] = useState([]);
  const [featuresMap, setFeaturesMap] = useState({});

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [error, setError] = useState(null);

  // Demo Modal States
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  // Enterprise Section States - DYNAMIC
  const [enterpriseSections, setEnterpriseSections] = useState([]);
  const [enterpriseLoading, setEnterpriseLoading] = useState(true);
  const [enterpriseError, setEnterpriseError] = useState(null);
  const [enterpriseProductIds, setEnterpriseProductIds] = useState([]);

  // Function to randomly select 1-3 products from all available products
  const selectRandomEnterpriseProducts = (allProducts, min = 1, max = 3) => {
    if (!allProducts || allProducts.length === 0) {
      return [];
    }

    // Randomly decide how many products to show (between min and max)
    const count = Math.floor(Math.random() * (max - min + 1)) + min;

    // Make sure we don't try to select more products than available
    const actualCount = Math.min(count, allProducts.length);

    // Shuffle and select random products
    const shuffled = [...allProducts].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, actualCount);

    // Return array of product IDs
    return selected.map(p => p.productId);
  };

  // Enterprise Carousel Ref and State
  const carouselRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);
  const productCarouselRef = useRef(null);
  const productAutoScrollRef = useRef(null);
  const [isProductCarouselHovered, setIsProductCarouselHovered] = useState(false);

  // Testimonials State
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [testimonialsError, setTestimonialsError] = useState(null);



  // Handle Try for Free button click
  const handleTryForFree = (productName) => {
    setSelectedProduct(productName);
    setIsDemoOpen(true);
  };

  // Scroll from other pages
  useEffect(() => {
    if (location.state?.scrollToProducts) {
      setTimeout(() => {
        const productsSection = document.getElementById('products');
        if (productsSection) productsSection.scrollIntoView({ behavior: 'smooth' });
      }, 120);

      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch all dynamic data
  useEffect(() => {
    fetchInitialData();
    fetchTestimonials();
  }, []);

  useEffect(() => {
    if (enterpriseProductIds.length > 0) {
      fetchEnterpriseData();
    }
  }, [enterpriseProductIds]);

  // Fetch products when category changes
  useEffect(() => {
    if (activeCategory && categories.length > 0) {
      fetchProductData(activeCategory);
    }
  }, [activeCategory, categories.length]);

  // Enterprise Carousel Auto-Scroll Effect
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || enterpriseLoading) return;

    const scrollOnePage = () => {
      if (!isCarouselHovered && carousel) {
        const slideWidth = carousel.querySelector('.enterprise-slide')?.offsetWidth + 20;
        if (!slideWidth) return;

        carousel.scrollLeft += slideWidth;

        const maxScroll = carousel.scrollWidth / 2;
        if (carousel.scrollLeft >= maxScroll - 50) {
          carousel.style.scrollBehavior = 'auto';
          carousel.scrollLeft = 0;
          setTimeout(() => {
            carousel.style.scrollBehavior = 'smooth';
          }, 50);
        }
      }
    };

    autoScrollIntervalRef.current = setInterval(scrollOnePage, 3000);

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [isCarouselHovered, enterpriseLoading]);

  // Product Carousel Auto-Scroll Effect
  useEffect(() => {
    const carousel = productCarouselRef.current;

    if (!carousel || productList.length === 0) {
      return;
    }

    const autoScroll = () => {
      if (!isProductCarouselHovered && carousel) {
        const cardWidth = 370;
        carousel.scrollLeft += cardWidth;

        if (carousel.scrollLeft >= carousel.scrollWidth - carousel.offsetWidth - 50) {
          carousel.style.scrollBehavior = 'auto';
          carousel.scrollLeft = 0;
          setTimeout(() => {
            carousel.style.scrollBehavior = 'smooth';
          }, 50);
        }
      }
    };

    productAutoScrollRef.current = setInterval(autoScroll, 2000);

    return () => {
      if (productAutoScrollRef.current) {
        clearInterval(productAutoScrollRef.current);
      }
    };
  }, [isProductCarouselHovered, productList]);


  useEffect(() => {
    if (!isAutoScrolling || testimonialsLoading || testimonials.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => {
        const maxIndex = Math.ceil(testimonials.length / 2) * 2 - 2;
        return (prev + 2) > maxIndex ? 0 : prev + 2;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoScrolling, testimonialsLoading, testimonials.length]);

  // Fetch Enterprise Products Data
  const fetchEnterpriseData = async () => {
    setEnterpriseLoading(true);
    setEnterpriseError(null);

    try {
      const enterpriseData = await Promise.all(
        enterpriseProductIds.map(async (productId) => {
          try {
            const product = await getProductById(productId);

            if (!product) {
              console.warn(`Product ${productId} not found`);
              return null;
            }

            let features = [];

            if (product.productHighlights) {
              features = product.productHighlights
                .split(/\r?\n/)
                .map(f => f.trim())
                .filter(f => f.length > 0);
            }

            if (features.length === 0) {
              try {
                const apiFeatures = await getFeatureByProduct(productId);
                if (apiFeatures && Array.isArray(apiFeatures)) {
                  features = apiFeatures
                    .map(f => f.featureName || f.feature_name)
                    .filter(f => f && f.trim().length > 0);
                }
              } catch (err) {
                console.warn(`Could not fetch features for product ${productId}:`, err);
              }
            }

            if (features.length === 0 && product.productOverview) {
              features = product.productOverview
                .split(/\r?\n/)
                .map(f => f.trim())
                .filter(f => f.length > 0)
                .slice(0, 3);
            }

            features = features.slice(0, 3);

            return {
              productId: product.productId,
              title: product.productName,
              features: features,
              description: product.productDescription,
              overview: product.productOverview,
              raw: product
            };
          } catch (err) {
            console.error(`Error fetching product ${productId}:`, err);
            return null;
          }
        })
      );

      const validData = enterpriseData.filter(item => item !== null);

      if (validData.length === 0) {
        setEnterpriseError('No enterprise products available');
      } else {
        setEnterpriseSections(validData);
        console.log('Enterprise sections loaded:', validData);
      }
    } catch (error) {
      console.error('Error fetching enterprise data:', error);
      setEnterpriseError('Failed to load enterprise products');
    } finally {
      setEnterpriseLoading(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      const categoryResponse = await getAllCategory();
      console.log('Categories fetched:', categoryResponse);

      setCategories(categoryResponse);

      if (categoryResponse && categoryResponse.length > 0) {
        const firstCategoryId = categoryResponse[0].categoryId;
        setActiveCategory(firstCategoryId);

        const productResponse = await getAllProducts(firstCategoryId);
        console.log('Initial products fetched:', productResponse);
        setProductList(productResponse || []);
        if (productResponse && productResponse.length > 0) {
          const randomIds = selectRandomEnterpriseProducts(productResponse, 1, 3);
          setEnterpriseProductIds(randomIds);
          console.log('🎲 Randomly selected enterprise products:', randomIds);
        }
      }



      const featureResponse = await getProductFeatures();
      console.log('Features fetched:', featureResponse);

      const fMap = {};
      if (featureResponse && Array.isArray(featureResponse)) {
        featureResponse.forEach(f => {
          if (f.productId && f.featureName) {
            if (!fMap[f.productId]) {
              fMap[f.productId] = [];
            }
            fMap[f.productId].push(f.featureName);
          }
        });
      }
      setFeaturesMap(fMap);
      console.log('Features map created:', fMap);

    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchProductData = async (categoryId) => {
    try {
      if (categoryId) {
        const productResponse = await getAllProducts(categoryId);
        console.log('Products fetched for category', categoryId, ':', productResponse);
        setProductList(productResponse || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductList([]);
    }
  };

  const fetchTestimonials = async () => {
    setTestimonialsLoading(true);
    setTestimonialsError(null);

    try {
      const data = await getActiveTestimonials();
      setTestimonials(data);
      console.log('✅ Testimonials loaded:', data.length);
    } catch (error) {
      console.error('❌ Error loading testimonials:', error);
      setTestimonialsError('Failed to load testimonials');
    } finally {
      setTestimonialsLoading(false);
    }
  };

  const handleGetInsights = async () => {
    if (!websiteUrl.trim()) {
      setError('Please enter a valid website URL');
      return;
    }

    let formatted = websiteUrl.trim();
    if (!formatted.startsWith('http')) {
      formatted = 'https://' + formatted;
    }

    setError(null);

    try {
      console.log('🔍 Tracking insight for URL:', formatted);
      const insightId = await trackSeoInsight(formatted);

      console.log('✅ SEO insight tracked successfully with ID:', insightId);
      storeInsightId(insightId);

      const user = JSON.parse(localStorage.getItem("user") || "null");
      console.log('👤 Current user:', user?.email || 'Not logged in');
      console.log('💾 Stored insight ID:', getStoredInsightId());

      navigate(`/seo-results?url=${encodeURIComponent(formatted)}`);

    } catch (trackingError) {
      console.error('⚠️ Failed to track insight:', trackingError);
      navigate(`/seo-results?url=${encodeURIComponent(formatted)}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleGetInsights();
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prevIndex) => {
      if (prevIndex === 0) {
        return Math.ceil(testimonials.length / 2) * 2 - 2;
      }
      return Math.max(0, prevIndex - 2);
    });
    setIsAutoScrolling(false);
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prevIndex) => {
      const maxIndex = Math.ceil(testimonials.length / 2) * 2 - 2;
      return (prevIndex + 2) > maxIndex ? 0 : prevIndex + 2;
    });
    setIsAutoScrolling(false);
  };

  const nextCategory = () => {
    const currentIndex = categories.findIndex(c => c.categoryId === activeCategory);
    const nextIndex = (currentIndex + 1) % categories.length;
    setActiveCategory(categories[nextIndex].categoryId);
  };

  const prevCategory = () => {
    const currentIndex = categories.findIndex(c => c.categoryId === activeCategory);
    const prevIndex = currentIndex === 0 ? categories.length - 1 : currentIndex - 1;
    setActiveCategory(categories[prevIndex].categoryId);
  };

  const scrollCarousel = (direction) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const slideWidth = carousel.querySelector('.enterprise-slide')?.offsetWidth + 20;
    if (!slideWidth) return;

    const scrollAmount = direction === 'left' ? -slideWidth : slideWidth;

    carousel.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });

    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = setInterval(() => {
        if (!isCarouselHovered && carousel) {
          const slideWidth = carousel.offsetWidth;
          carousel.scrollLeft += slideWidth;

          const maxScroll = carousel.scrollWidth / 2;
          if (carousel.scrollLeft >= maxScroll) {
            carousel.style.scrollBehavior = 'auto';
            carousel.scrollLeft = 0;
            setTimeout(() => {
              carousel.style.scrollBehavior = 'smooth';
            }, 30);
          }
        }
      }, 2000);
    }
  };

  const scrollProductCarousel = (direction) => {
    const carousel = productCarouselRef.current;
    if (!carousel) return;

    const cardWidth = 370;
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;

    carousel.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };


  return (
    <div className="homepage bg-white">

      <Navbar />

      {/* HERO SECTION */}
      <section className="hero-section">

        <div className="container px-0 ">
          <h1 className="hero-title">
            Unleasing digital <span className='cs-span'>Intelligence to </span><br />
            transform marketing and growth
          </h1>

          <div className="search-container d-flex align-items-center">
            <input
              type="text"
              className="search-input flex-grow-1"
              placeholder="Enter website or URL"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button className="btn btn-insights" onClick={handleGetInsights}>
              Get insights
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mt-4 mx-auto" style={{ maxWidth: "600px" }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <h2 id="products" className="toolkit-subtitle">Products For Every Need</h2>

          <div className="toolkit-tabs">
            {categories.map((cat) => (
              <button
                key={cat.categoryId}
                className={`toolkit-tab ${activeCategory === cat.categoryId ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.categoryId)}
              >
                {cat.categoryName}
              </button>
            ))}
          </div>

          <div className="products-section">
            <div className="products-carousel-wrapper">
              <button
                className="product-carousel-arrow product-arrow-left"
                onClick={() => scrollProductCarousel('left')}
              >
                <ChevronLeft size={24} />
              </button>

              {productList.length > 0 ? (
                productList.map((prod) => (
                  <div key={prod.productId} className="product-card">
                    <div className="product-header">
                      <h4 className="product-name">{prod.productName}</h4>
                      <p className="product-description">{prod.productDescription}</p>
                    </div>

                    {featuresMap[prod.productId] && featuresMap[prod.productId].length > 0 && (
                      <ul className="product-features" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {featuresMap[prod.productId].slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="product-feature" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <Check size={16} className="feature-check-icon" style={{ flexShrink: 0 }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="product-actions">
                      <Link to={`/product/${prod.productId}`} className="btn btn-outline-primary">
                        Learn more
                      </Link>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleTryForFree(prod.productName)}
                      >
                        Try for free
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="product-error-container">
                  <div className="product-error-card">
                    <div className="product-error-icon-wrapper">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                        <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
                        <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" />
                      </svg>
                    </div>
                    <h3 className="product-error-title">No Products Available</h3>
                    <p className="product-error-message">
                      No products are currently available for this category. Please try another category or check back later.
                    </p>
                    <button
                      className="product-retry-btn"
                      onClick={() => fetchProductData(activeCategory)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M23 4v6h-6M1 20v-6h6" />
                        <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                      </svg>
                      Retry Loading
                    </button>
                  </div>
                </div>
              )}
              <button
                className="product-carousel-arrow product-arrow-right"
                onClick={() => scrollProductCarousel('right')}
              >
                <ChevronRight size={24} />
              </button>
            </div>

            <div className="category-navigation">
              <button className="nav-arrow prev" onClick={prevCategory}>
                <ChevronLeft size={20} />
              </button>
              <button className="nav-arrow next" onClick={nextCategory}>
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ENTERPRISE INFINITE CAROUSEL SECTION */}
      <section className="enterprise-carousel-section">
        <div className="enterprise-carousel-wrapper">
          {enterpriseLoading ? (
            <div className="enterprise-loading" style={{
              textAlign: 'center',
              padding: '100px 0',
              fontSize: '18px',
              color: '#666'
            }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '20px' }}>Loading enterprise products...</p>
            </div>
          ) : enterpriseError ? (
            <div className="enterprise-error-container">
              <div className="enterprise-error-card">
                <div className="error-icon-wrapper">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
                    <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" />
                  </svg>
                </div>
                <h3 className="error-title">Unable to Load Enterprise Products</h3>
                <p className="error-message">{enterpriseError}</p>
                <button
                  className="enterprise-retry-btn"
                  onClick={fetchEnterpriseData}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 4v6h-6M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                  Retry Loading
                </button>
              </div>
            </div>
          ) : enterpriseSections.length > 0 ? (
            <>
              <button
                className="carousel-arrow carousel-arrow-left"
                onClick={() => scrollCarousel('left')}
                onMouseEnter={() => setIsCarouselHovered(true)}
                onMouseLeave={() => setIsCarouselHovered(false)}
              >
                <ChevronLeft size={24} />
              </button>

              <div
                ref={carouselRef}
                className="enterprise-carousel-track"
                onMouseEnter={() => setIsCarouselHovered(true)}
                onMouseLeave={() => setIsCarouselHovered(false)}
              >
                {/* Original slides */}
                {enterpriseSections.map((section, index) => (
                  <div key={`original-${index}`} className="enterprise-slide">
                    <div className="enterprise-slide-content">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '30px' }}>
                          <span style={{ fontSize: '32px' }}>🚀</span>
                          <span style={{ fontSize: '16px', color: '#a0aec0' }}>
                            Elexyt Enterprise
                          </span>
                        </div>
                        <h2 className="enterprise-slide-title">{section.title}</h2>

                        <ul className="enterprise-feature-list">
                          {section.features.map((feature, idx) => (
                            <li key={idx} className="enterprise-feature-item">
                              <Check size={24} className="enterprise-check-icon" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="enterprise-product-icon-box">
                          <span>📊</span>
                        </div>
                      </div>

                      <Link to={`/product/${section.productId}`} className="enterprise-discover-btn">
                        Discover More
                        <ChevronRight size={16} />
                      </Link>
                    </div>

                    <div className="enterprise-analytics-card">
                      <div className="analytics-overview-box">
                        <h3 className="analytics-overview-title">Website Quality Metrics</h3>
                        <div className="analytics-stats-grid">
                          <div className="analytics-stat-item">
                            <h4>Page Views</h4>
                            <p>42,580</p>
                          </div>
                          <div className="analytics-stat-item">
                            <h4>Bounce Rate</h4>
                            <p>32%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Duplicate slides for infinite scrolling */}
                {enterpriseSections.map((section, index) => (
                  <div key={`duplicate-${index}`} className="enterprise-slide">
                    <div className="enterprise-slide-content">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '30px' }}>
                          <span style={{ fontSize: '32px' }}>🚀</span>
                          <span style={{ fontSize: '16px', color: '#a0aec0' }}>
                            Elexyt Enterprise
                          </span>
                        </div>
                        <h2 className="enterprise-slide-title">{section.title}</h2>

                        <ul className="enterprise-feature-list">
                          {section.features.map((feature, idx) => (
                            <li key={idx} className="enterprise-feature-item">
                              <Check size={24} className="enterprise-check-icon" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <div className="enterprise-product-icon-box">
                          <span>📊</span>
                        </div>
                      </div>

                      <Link to={`/product/${section.productId}`} className="enterprise-discover-btn">
                        Discover More
                        <ChevronRight size={16} />
                      </Link>
                    </div>

                    <div className="enterprise-analytics-card">
                      <div className="analytics-overview-box">
                        <h3 className="analytics-overview-title">Website Quality Metrics</h3>
                        <div className="analytics-stats-grid">
                          <div className="analytics-stat-item">
                            <h4>Page Views</h4>
                            <p>42,580</p>
                          </div>
                          <div className="analytics-stat-item">
                            <h4>Bounce Rate</h4>
                            <p>32%</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className="carousel-arrow carousel-arrow-right"
                onClick={() => scrollCarousel('right')}
                onMouseEnter={() => setIsCarouselHovered(true)}
                onMouseLeave={() => setIsCarouselHovered(false)}
              >
                <ChevronRight size={24} />
              </button>
            </>
          ) : null}
        </div>
      </section>




      {/* Testimonial Section */}
      {/* Testimonial Section */}
      <section className="testimonial-section py-5">
        <div className="container">
          <h2 className="text-center fw-bold mb-5">What our clients say</h2>

          {testimonialsLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-white" role="status">
                <span className="visually-hidden">Loading testimonials...</span>
              </div>
            </div>
          ) : testimonialsError ? (
            <div className="enterprise-error-container">
              <div className="enterprise-error-card">
                <div className="error-icon-wrapper">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
                    <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2" />
                  </svg>
                </div>
                <h3 className="error-title">Unable to Load Testimonials</h3>
                <p className="error-message">{testimonialsError}</p>
                <button
                  className="enterprise-retry-btn"
                  onClick={fetchTestimonials}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 4v6h-6M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                  </svg>
                  Retry Loading
                </button>
              </div>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="enterprise-error-container">
              <div className="enterprise-error-card">
                <div className="error-icon-wrapper">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <path d="M8 12h8M12 8v8" strokeWidth="2" />
                  </svg>
                </div>
                <h3 className="error-title">No Testimonials Available</h3>
                <p className="error-message">
                  Customer testimonials will appear here once they are added. Check back soon!
                </p>
              </div>
            </div>
          ) : (
            <div className="testimonial-carousel-container">
              <div className="testimonial-navigation">
                <button className="nav-arrow" onClick={prevTestimonial}>
                  <ChevronLeft size={20} />
                </button>

                <div className="testimonial-slideshow">
                  <div
                    className="testimonial-slides"
                    style={{
                      transform: `translateX(-${Math.floor(currentTestimonial / 2) * 100}%)`,
                      transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    {/* ✅ Show 2 separate cards in a row */}
                    {Array.from({ length: Math.ceil(testimonials.length / 2) }, (_, groupIndex) => (
                      <div key={groupIndex} className="testimonial-slide-pair">
                        {testimonials.slice(groupIndex * 2, groupIndex * 2 + 2).map((testimonial, index) => (
                          <div key={testimonial.id || index} className="testimonial-card-separate">
                            <div className="testimonial-text">
                              "{testimonial.comment}"
                            </div>
                            <div className="testimonial-author-section">
                              <div className="author-info">
                                <h5>{testimonial.name}</h5>
                                <p>{testimonial.designation}{testimonial.org ? `, ${testimonial.org}` : ''}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <button className="nav-arrow" onClick={nextTestimonial}>
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="testimonial-indicators">
                {Array.from({ length: Math.ceil(testimonials.length / 2) }, (_, index) => (
                  <button
                    key={index}
                    className={`indicator ${Math.floor(currentTestimonial / 2) === index ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentTestimonial(index * 2);
                      setIsAutoScrolling(false);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <DemoRequestModal
        isOpen={isDemoOpen}
        onClose={() => setIsDemoOpen(false)}
        productName={selectedProduct}
      />

      <SecondaryFooter />

    </div>
  );
};

export default HomePageComponent;